import { Button, Input, Label } from '@repo/ui';
import { type FormEvent, type ReactElement, useState } from 'react';

interface NameFormModalProps {
  readonly title: string;
  readonly label: string;
  readonly placeholder: string;
  /** 입력 초기값 — 이름 변경에 쓴다. 기본값은 빈 문자열(추가). */
  readonly initialName?: string;
  /** 제출 버튼 문구. 기본값은 "추가하기"(추가). */
  readonly submitLabel?: string;
  /** 입력 상한 — 계약 이름 스키마의 max 와 같게 준다. 넘친 값은 서버가 영어 검증 문구로 거부한다. */
  readonly maxLength?: number;
  readonly isPending: boolean;
  readonly errorMessage: string | null;
  readonly onSubmit: (name: string) => void;
  readonly onClose: () => void;
}

/** 이름 하나를 입력받는 추가·이름 변경 모달 — 카테고리·인쇄 방식·기획전 목적이 공유한다. */
export const NameFormModal = ({
  title,
  label,
  placeholder,
  initialName = '',
  submitLabel = '추가하기',
  maxLength,
  isPending,
  errorMessage,
  onSubmit,
  onClose,
}: NameFormModalProps): ReactElement => {
  const [name, setName] = useState(initialName);

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();

    if (name.trim()) {
      onSubmit(name.trim());
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/40">
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="w-96 rounded-lg border bg-background p-5 shadow-lg"
      >
        <p className="font-medium">{title}</p>

        {errorMessage ? (
          <p role="alert" className="mt-2 text-sm text-destructive">
            {errorMessage}
          </p>
        ) : null}

        <form onSubmit={handleSubmit} className="mt-4 flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="name-form-modal-input">{label}</Label>
            <Input
              id="name-form-modal-input"
              placeholder={placeholder}
              value={name}
              maxLength={maxLength}
              onChange={(event) => setName(event.target.value)}
              autoFocus
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" size="sm" onClick={onClose}>
              취소
            </Button>
            <Button type="submit" size="sm" isLoading={isPending} disabled={!name.trim()}>
              {submitLabel}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
